import React, { useEffect, useState, useRef } from "react";
import {
  PiCaretDoubleLeft,
  PiCaretLeft,
  PiCaretDoubleRight,
  PiCaretRight,
  PiMagnifyingGlassLight,
  PiDotsThreeVertical,
} from "react-icons/pi";
import LoadingIcons from "react-loading-icons";
import { createPopup } from "coaz-ui";
import { getScreenSize, sc } from "../constants";

const normalize = (response, searchKey, page, mapItem) => {
  if (!response) return null;
  if (Array.isArray(response.users)) return response;
  const map = mapItem || ((row) => row);

  if (Array.isArray(response)) {
    const key = (searchKey || "").trim().toLowerCase();
    const all = response
      .map(map)
      .filter(
        (user) =>
          user &&
          (!key ||
            `${user.firstname ?? ""} ${user.lastname ?? ""} ${user.name ?? ""}`
              .toLowerCase()
              .includes(key))
      );
    const start = page.pageNo * page.pageSize;
    return {
      users: all.slice(start, start + page.pageSize),
      pageNo: page.pageNo,
      pageSize: page.pageSize,
      totalElements: all.length,
      totalPages: Math.max(1, Math.ceil(all.length / page.pageSize)),
      last: start + page.pageSize >= all.length,
    };
  }

  if (Array.isArray(response.content)) {
    return {
      users: response.content.map(map),
      pageNo: response.pageNo ?? response.number ?? 0,
      pageSize: response.pageSize ?? response.size ?? page.pageSize,
      totalElements: response.totalElements ?? response.content.length,
      totalPages: response.totalPages ?? 1,
      last: response.last ?? true,
    };
  }
  return null;
};

const UserItems = ({ onSelect, search, buttons, version, selectedId, mapItem }) => {
  const [users, setUsers] = useState([]);
  const [pageNo, setPageNo] = useState(0);
  const [pageSize, setPageSize] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [last, setLast] = useState(true);
  const [page, setPage] = useState({ pageNo: 0, pageSize: 10 });
  const [loading, setLoading] = useState(!!search);
  const [searchKey, setSearchKey] = useState("");
  const searchKeyRef = useRef("");

  const handleChange = (e) => {
    const value = e.target.value;
    setSearchKey(value);
    searchKeyRef.current = value;
    handleFirst(e); 
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleFirst(e);
  };

  const handleFirst = (e) => {
    if (e) e.preventDefault();
    setPage({ ...page, pageNo: 0 });
  };

  const handlePrevious = (e) => {
    if (e) e.preventDefault();
    if (page.pageNo > 0) setPage({ ...page, pageNo: page.pageNo - 1 });
  };

  const handleNext = (e) => {
    if (e) e.preventDefault();
    if (page.pageNo < totalPages - 1) setPage({ ...page, pageNo: page.pageNo + 1 });
  };

  const handleLast = (e) => {
    if (e) e.preventDefault();
    if (page.pageNo < totalPages - 1 && !last) {
      setPage({ ...page, pageNo: totalPages - 1 });
    }
  };

  useEffect(() => {
    (async () => {
      if (!search) return;
      setLoading(true);

      try {
        const response = normalize(await search(searchKey, page), searchKey, page, mapItem);

        if (searchKey === searchKeyRef.current) {
          if (response && response.users) {
            setUsers(response.users);
            setPageNo(response.pageNo);
            setPageSize(response.pageSize);
            setTotalElements(response.totalElements);
            setTotalPages(response.totalPages);
            setLast(response.last);
          } else {
            setUsers([]);
          }
        }
      } catch (error) {
        console.log(error);
        setUsers([]);
      }

      setLoading(false);
    })();
  }, [page, version]);

  const disablePrevious = !pageNo || pageNo < 1;

  return (
    <div className="flex flex-col w-full h-auto space-y-2">
      
      <div
        onKeyDown={handleKeyDown}
        className="flex flex-row w-full overflow-hidden border rounded-full h-fit bg-white"
      >
        <button
          type="button"
          onClick={(e) => handleFirst(e)}
          aria-label="Search"
          className="flex w-10 h-10 justify-center shrink-0 items-center text-[rgb(170,170,170)]"
        >
          <PiMagnifyingGlassLight size={20} />
        </button>
        <input
          type="text"
          name="username"
          value={searchKey}
          onChange={handleChange}
          placeholder="Search..."
          autoComplete="off"
          className="flex w-full h-10 p-2 overflow-hidden text-sm focus:outline-none whitespace-nowrap"
        />
      </div>

      {users && totalElements > pageSize && (
        <div className="flex flex-col items-center justify-center w-full space-y-2 overflow-hidden ml:flex-row h-fit ml:justify-between ml:space-y-0 ml:space-x-2">
          <p className="flex shrink-0 text-xs text-[rgb(145,145,145)] tracking-wider">
            {`Showing ${users.length > 0 ? pageNo * pageSize + 1 : 0} to ${
              pageNo * pageSize + users.length
            } of ${totalElements}`}
          </p>

          <div className="flex flex-row w-fit h-fit shrink-0 space-x-2 text-[rgb(68,71,70)]">
            <button
              type="button"
              disabled={disablePrevious}
              onClick={handleFirst}
              aria-label="First page"
              className={`flex w-10 h-10 ${
                disablePrevious ? "text-[rgb(170,170,170)]" : "hover:bg-[rgba(0,0,0,.04)]"
              } rounded-full`}
            >
              <PiCaretDoubleLeft size={16} className="flex m-auto" />
            </button>
            <button
              type="button"
              disabled={disablePrevious}
              onClick={handlePrevious}
              aria-label="Previous page"
              className={`flex w-10 h-10 ${
                disablePrevious ? "text-[rgb(170,170,170)]" : "hover:bg-[rgba(0,0,0,.04)]"
              } rounded-full`}
            >
              <PiCaretLeft size={16} className="flex m-auto" />
            </button>
            <button
              type="button"
              disabled={last}
              onClick={handleNext}
              aria-label="Next page"
              className={`flex w-10 h-10 ${
                last ? "text-[rgb(170,170,170)]" : "hover:bg-[rgba(0,0,0,.04)]"
              } rounded-full`}
            >
              <PiCaretRight size={16} className="flex m-auto" />
            </button>
            <button
              type="button"
              disabled={last}
              onClick={handleLast}
              aria-label="Last page"
              className={`flex w-10 h-10 ${
                last ? "text-[rgb(170,170,170)]" : "hover:bg-[rgba(0,0,0,.04)]"
              } rounded-full`}
            >
              <PiCaretDoubleRight size={16} className="flex m-auto" />
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center w-full h-full">
          <LoadingIcons.SpinningCircles width={64} height={64} fill="rgb(0,175,240)" />
        </div>
      ) : users && users.length > 0 ? (
        <div className="flex flex-col w-full h-auto space-y-2">
          {users.map((user, i) => (
            <UserItem
              key={user.ref?.id ?? i}
              user={user}
              onSelect={onSelect}
              buttons={buttons}
              page={page}
              setPage={setPage}
              selected={selectedId != null && (user.ref?.id ?? user.id) === selectedId}
            />
          ))}
        </div>
      ) : (
        <p className="text-sm text-[rgb(145,145,145)] p-2">No users found.</p>
      )}
    </div>
  );
};

export default UserItems;

const UserItem = ({ user, onSelect, buttons, page, setPage, selected }) => {
  const [highlighted, setHighlighted] = useState(false);
  const moreRef = useRef(null); 

  if (!user) return null;

  const firstname = user.firstname ?? "";
  const lastname = user.lastname ?? "";
  const fullName = `${firstname} ${lastname}`.trim() || user.name || "";
  const initials =
    (firstname.charAt(0) + lastname.charAt(0)).toUpperCase() ||
    fullName.charAt(0).toUpperCase();

  const showMenu =
    (highlighted || getScreenSize() <= sc.xs) && buttons && buttons.length > 0;

  const openMenu = (e) => {
    e.stopPropagation();
    createPopup({
      buttonRef: moreRef,
      Component: (
        <div className="flex flex-col w-fit h-fit text-left text-sm font-[arial] text-[rgb(68,71,70)]">
          {buttons.map((button, i) => (
            <button
              key={i}
              onClick={() => button.handler && button.handler(user, page, setPage)}
              className="flex flex-row w-full px-2 h-8 space-x-2 items-center hover:bg-[rgb(234,235,239)] shrink-0"
            >
              {button.Icon && <button.Icon size={20} className="flex shrink-0" />}
              {button.name && (
                <p className="w-full overflow-hidden text-left whitespace-nowrap overflow-ellipsis">
                  {button.name}
                </p>
              )}
            </button>
          ))}
        </div>
      ),
    });
  };

  return (
    <div className="flex flex-row w-full h-auto overflow-hidden">
      <div
        onMouseEnter={() => setHighlighted(true)}
        onMouseLeave={() => setHighlighted(false)}
        className={`flex flex-row w-full p-2 items-center justify-between hover:bg-[rgba(0,0,0,.04)] rounded-full overflow-hidden ${
          selected ? "bg-[rgba(0,175,240,.12)]" : ""
        }`}
      >
        <div
          onClick={(e) => {
            e.preventDefault();
            onSelect && onSelect(user);
          }}
          className={`flex flex-row w-full items-center space-x-2 overflow-hidden ${
            onSelect ? "cursor-pointer" : ""
          }`}
        >

          <p className="flex w-10 h-10 shrink-0 rounded-full text-xl items-center justify-center text-white bg-[rgb(0,175,240)]">
            {initials}
          </p>

          <div className="flex flex-col w-full overflow-hidden h-fit">
            <p className="text-sm text-[rgb(68,71,70)] whitespace-nowrap overflow-hidden overflow-ellipsis capitalize">
              {fullName.toLowerCase()}
            </p>
            <div className="flex flex-row w-full space-x-2">
              {user.userType && (
                <p className="text-xs text-[rgb(145,145,145)] whitespace-nowrap overflow-hidden overflow-ellipsis capitalize">
                  {user.userType.name}
                </p>
              )}
              {user.status && (
                <p
                  className={`text-xs ${
                    user.status === "ACTIVE"
                      ? "text-green-600"
                      : user.status === "OTP"
                      ? "text-yellow-400"
                      : "text-[rgb(145,145,145)]"
                  } whitespace-nowrap overflow-hidden overflow-ellipsis capitalize`}
                >
                  {user.status.toLowerCase()}
                </p>
              )}
            </div>
          </div>
        </div>

        {showMenu && (
          <button
            ref={moreRef}
            onClick={openMenu}
            aria-label="More"
            className="flex w-10 h-10 items-center justify-center shrink-0 hover:bg-[rgba(0,0,0,.06)] rounded-full"
          >
            <PiDotsThreeVertical size={20} />
          </button>
        )}
      </div>
    </div>
  );
};